import { EmptyState, FlexColumn, FlexRow, MaterialIcon } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { Card } from 'baseui/card';
import { Checkbox } from 'baseui/checkbox';
import { DatePicker, TimePicker } from 'baseui/datepicker';
import { StyledDivider } from 'baseui/divider';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Textarea } from 'baseui/textarea';
import { LabelSmall, ParagraphXSmall } from 'baseui/typography';
import { useMemo } from 'react';
import { useSetRecoilState } from 'recoil';
import { useFormFieldConfig } from '../../hooks/useFormFieldConfig';
import { useStyles } from '../../hooks/useStyles';
import { selectedFormFieldState } from '../../storage/formStates';
import { FormField } from '../../types/forms';
import { getFieldTypeName } from '../../utils/forms';
import EnhancedLabeledSelect from '../ui/EnhancedLabeledSelect';
import StyledTooltip from '../ui/StyledTooltip';

type Props = {
  field: FormField;
  index: number;
};

export function FormFieldConfig({ field, index }: Readonly<Props>) {
  const { theme, css } = useStyles();
  const setSelectedFormField = useSetRecoilState(selectedFormFieldState);
  const fieldConfig = useFormFieldConfig(field.fieldType);

  const fieldLabel = `${field.name} ${field.required ? '*' : ''}`;

  const fieldTypeName = useMemo(() => {
    return getFieldTypeName(field.fieldType);
  }, [field.fieldType]);

  const helpText = (() => {
    return field.help_text ? (
      <ParagraphXSmall
        marginTop={theme.sizing.scale0}
        marginBottom={theme.sizing.scale300}
        color={theme.colors.contentTertiary}
      >
        {field.help_text}
      </ParagraphXSmall>
    ) : null;
  })();

  return (
    <Card
      overrides={{
        Root: {
          style: {
            borderLeftWidth: '1px',
            borderRightWidth: '1px',
            borderTopWidth: '1px',
            borderBottomWidth: '1px',
          },
        },
        Contents: {
          style: {
            marginTop: 0,
            marginRight: 0,
            marginBottom: 0,
            marginLeft: 0,
          },
        },
        Body: {
          style: {
            marginBottom: 0,
          },
        },
      }}
    >
      <FlexRow
        classNames={css({
          padding: `${theme.sizing.scale200} ${theme.sizing.scale400}`,
          alignItems: 'center',
          justifyContent: 'space-between',
        })}
      >
        <LabelSmall>{fieldTypeName}</LabelSmall>
        <StyledTooltip
          content={'Editar'}
          showArrow={false}
        >
          <Button
            size="mini"
            shape="square"
            onClick={() => {
              setSelectedFormField({
                index,
                id: field._id,
                uncreated: false,
              });
            }}
            kind="tertiary"
          >
            <MaterialIcon
              name={'edit'}
              size="14px"
            />
          </Button>
        </StyledTooltip>
      </FlexRow>
      <StyledDivider
        $size="cell"
        style={{ marginTop: 0, marginBottom: 0 }}
      />
      <FlexColumn classNames={css({ padding: theme.sizing.scale400 })}>
        <FlexColumn gap={theme.sizing.scale200}>
          {fieldConfig.renderType === 'input' && (
            <FormControl
              label={fieldLabel}
              caption={field.help_text}
              overrides={{
                LabelContainer: {
                  style: {
                    marginTop: 0,
                    marginBottom: 0,
                  },
                },
                ControlContainer: {
                  style: { marginBottom: 0 },
                },
              }}
            >
              <Input
                endEnhancer={field.sufix}
                name={field.name}
                placeholder={field.name}
                required={field.required}
                size="compact"
                startEnhancer={field.prefix}
                type={fieldConfig.inputType}
                value={field.default}
              />
            </FormControl>
          )}
          {fieldConfig.renderType === 'textarea' && (
            <FormControl
              label={fieldLabel}
              caption={field.help_text}
              overrides={{
                LabelContainer: {
                  style: {
                    marginTop: 0,
                    marginBottom: 0,
                  },
                },
                ControlContainer: {
                  style: { marginBottom: 0 },
                },
              }}
            >
              <Textarea
                name={field.name}
                placeholder={field.name}
                required={field.required}
                size="compact"
                value={field.default}
              />
            </FormControl>
          )}
          {fieldConfig.renderType === 'checkbox' && (
            <FormControl
              caption={field.help_text}
              overrides={{
                LabelContainer: {
                  style: {
                    marginTop: 0,
                    marginBottom: 0,
                  },
                },
                ControlContainer: {
                  style: { marginBottom: 0 },
                },
              }}
            >
              <Checkbox checked={!!field.default}>{field.name}</Checkbox>
            </FormControl>
          )}
          {fieldConfig.renderType === 'canvas' && (
            <FlexRow centered>
              <FlexColumn
                gap={theme.sizing.scale200}
                classNames={css({
                  width: '100%',
                  maxWidth: '300px',
                })}
              >
                <LabelSmall>{fieldLabel}</LabelSmall>
                <Block
                  className={css({
                    height: '100px',
                    backgroundColor: theme.colors.inputFill,
                    borderRadius: theme.borders.inputBorderRadius,
                  })}
                >
                  <EmptyState>
                    <MaterialIcon
                      name={(() => {
                        if (field.fieldType === 'canvas') return 'stylus_note';
                        if (field.fieldType === 'image') return 'image';
                        if (field.fieldType === 'file') return 'file';
                        if (field.fieldType === 'signature') return 'signature';
                        return '';
                      })()}
                      size="40px"
                      color={theme.colors.contentTertiary}
                    />
                  </EmptyState>
                </Block>
                {helpText}
              </FlexColumn>
            </FlexRow>
          )}
          {fieldConfig.renderType === 'date' && (
            <FlexColumn gap={theme.sizing.scale200}>
              <LabelSmall>{fieldLabel}</LabelSmall>
              <FlexRow gap={theme.sizing.scale200}>
                <div className={css({ width: '120px' })}>
                  <DatePicker
                    size="compact"
                    value={new Date()}
                    onChange={undefined}
                    mask="9999/99/99"
                  />
                </div>
              </FlexRow>
              {helpText}
            </FlexColumn>
          )}
          {fieldConfig.renderType === 'time' && (
            <FlexColumn gap={theme.sizing.scale200}>
              <LabelSmall>{fieldLabel}</LabelSmall>
              <div className={css({ width: '120px' })}>
                <TimePicker
                  size="compact"
                  value={new Date()}
                  onChange={undefined}
                />
              </div>
              {helpText}
            </FlexColumn>
          )}
          {fieldConfig.renderType === 'datetime' && (
            <FlexColumn gap={theme.sizing.scale200}>
              <LabelSmall>{fieldLabel}</LabelSmall>
              <FlexRow gap={theme.sizing.scale200}>
                <div className={css({ width: '120px' })}>
                  <DatePicker
                    size="compact"
                    value={new Date()}
                    onChange={undefined}
                    mask="9999/99/99"
                  />
                </div>
                <div className={css({ width: '120px' })}>
                  <TimePicker
                    size="compact"
                    value={new Date()}
                    onChange={undefined}
                  />
                </div>
              </FlexRow>
              {helpText}
            </FlexColumn>
          )}
          {fieldConfig.renderType === 'select' && (
            <FlexColumn gap={theme.sizing.scale200}>
              <LabelSmall>{fieldLabel}</LabelSmall>
              {field.options && field.options.length > 0 && (
                <FlexColumn gap={0}>
                  {field.options.map((option, index) => (
                    <Checkbox key={index}>{option.text}</Checkbox>
                  ))}
                </FlexColumn>
              )}
              {helpText}
            </FlexColumn>
          )}
          {fieldConfig.renderType === 'radio' && (
            <>
              <EnhancedLabeledSelect
                label={field.name}
                placeholder="Selecciona..."
                required={field.required}
                options={field.options?.map((option) => ({ id: option.value, label: option.text }))}
                onChange={() => {}}
              />
              {helpText}
            </>
          )}
        </FlexColumn>
      </FlexColumn>
    </Card>
  );
}
